import { useState, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, getAuthHeaders } from "../utils/api";

export default function ProtectedRoute({ children, requireAdmin = false }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Helper to validate user data
  const isValidUser = (data) => {
    if (!data || typeof data !== 'object') return false;
    // Check if it's a Pydantic validation error
    if ('type' in data && 'loc' in data && 'msg' in data) return false;
    // Check if it has expected user fields
    return typeof data.email === 'string' || typeof data.name === 'string' || typeof data.user_id === 'string';
  };

  useEffect(() => {
    // If user data passed from Login/AuthCallback, validate and use it
    if (location.state?.user && isValidUser(location.state.user)) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      // Clear location state to prevent stale data on refresh
      window.history.replaceState({}, document.title);
      return;
    }

    // CRITICAL: If returning from OAuth callback, skip the /me check
    // AuthCallback will exchange the session_id and establish the session first
    if (window.location.hash?.includes('session_id=')) {
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: getAuthHeaders(),
          timeout: 10000  // 10 second timeout
        });
        const userData = response.data;
        
        // Validate the user data
        if (!isValidUser(userData)) {
          console.warn('Invalid user data from /auth/me:', userData);
          localStorage.removeItem('session_token');
          setIsAuthenticated(false);
          return;
        }
        
        setUser(userData);
        
        // Check admin requirement
        if (requireAdmin && userData?.role !== 'admin') {
          setIsAuthenticated(false);
          return;
        }
        
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        // Clear any invalid session token
        localStorage.removeItem('session_token');
        setIsAuthenticated(false);
      }
    };

    // Small delay to ensure cookies are set after login
    const timer = setTimeout(checkAuth, 100);
    return () => clearTimeout(timer);
  }, [location.state, requireAdmin]);

  // Loading state - show spinner with better mobile styling
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B] p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-slate-400 text-sm">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin required but user is not admin
  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
